<template>
  <SSection
    :title="item?.name"
    title-tag="h1"
    :has-separator="false"
    :desktop-full="true"
  >
    <TText tag="h2" classes="mb-0">
      {{ item?.village.name }}, {{ item?.province.name }}
    </TText>

    <div class="mt-1 flex items-center justify-between md:justify-start">
      <div v-if="item.isReviewsEnabled" class="inline-flex items-center mr-4">
        <TStars :stars="item?.review?.averageReviews" />
        <TText class="ml-1" classes="mb-0" color="text-gray-600"
          >({{ item?.review?.totalReviews }})</TText
        >
      </div>
      <div
        v-if="item.reviews?.googleData.rating"
        class="inline-flex items-center"
      >
        <UButton
          color="neutral"
          variant="soft"
          @click="toogleGoogleReviewsModal()"
        >
          <TStarsGoogle
            compact
            :stars="item.reviews?.googleData.rating"
            :comments="item.reviews?.googleData.reviewsNumber"
          />
        </UButton>
        <UModal v-model:open="showGoogleReviews" :fullscreen="isMobile">
          <template #body>
            <SReviewsGoogle v-bind="item.reviews?.googleData" />
          </template>
        </UModal>
      </div>
    </div>

    <div class="grid gap-x-5 grid-cols-2 w-fit sm:flex sm:gap-x-8 mt-4">
      <div class="flex items-center m-1">
        <div
          v-if="item.rentType === 'FULL' || item.rentType === 'BOTH'"
          class="mr-1 flex items-center justify-center"
        >
          <UIcon
            :name="'i-heroicons-home'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div
          v-if="item.rentType === 'ROOMS' || item.rentType === 'BOTH'"
          class="mr-1 flex items-center justify-center"
        >
          <UIcon
            :name="'i-fluent-conference-room-20-regular'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div>
          <TText size="xs" leading="none" classes="mb-0">
            {{
              (item.rentType === 'FULL' &&
                t('views.cottage.visualInformation.rentTypeFull')) ||
              (item.rentType === 'ROOMS' &&
                t('views.cottage.visualInformation.rentTypeRooms')) ||
              (item.rentType === 'BOTH' &&
                t('views.cottage.visualInformation.rentTypeBoth'))
            }}</TText
          >
        </div>
      </div>
      <div class="flex items-center m-1">
        <div class="mr-1 flex items-center justify-center">
          <UIcon
            :name="'i-heroicons-user'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div>
          <TText size="xs" leading="none" classes="mb-0">
            {{
              item?.capacity.min
                ? `${item?.capacity.min} - ${item?.capacity.max} ${t(
                    'views.cottage.visualInformation.capacity',
                  )}`
                : `${item?.capacity.max} ${t(
                    'views.cottage.visualInformation.capacity',
                  )}`
            }}
          </TText>
        </div>
      </div>
      <div class="flex items-center m-1">
        <div class="mr-1 flex items-center justify-center">
          <UIcon
            :name="'i-fluent-door-tag-20-regular'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div>
          <TText size="xs" leading="none" classes="mb-0">
            {{
              item.numRooms > 1
                ? t('views.cottage.visualInformation.numRooms', item.numRooms)
                : t('views.cottage.visualInformation.room')
            }}</TText
          >
        </div>
      </div>
      <div class="flex items-center m-1">
        <div class="mr-1 flex items-center justify-center">
          <UIcon
            :name="'i-fluent-bed-24-regular'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div>
          <TText size="xs" leading="none" classes="mb-0">
            {{
              item.numBeds > 1
                ? t('views.cottage.visualInformation.numBeds', item.numBeds)
                : t('views.cottage.visualInformation.bed')
            }}</TText
          >
        </div>
      </div>
    </div>
    <div class="flex flex-wrap gap-2">
      <UBadge
        v-if="item.hasVerifiedCalendar"
        class="mt-4"
        color="neutral"
        variant="subtle"
        size="md"
        icon="i-lucide-calendar-check"
        >{{ t('views.cottage.tags.verifiedCalendar') }}</UBadge
      >
      <UBadge
        v-if="item.salesService.isOnlineBookingActive"
        data-test="onlineBookingBadge"
        class="mt-4"
        color="info"
        variant="subtle"
        size="md"
        :icon="
          item.salesService.confirmationType === 'IMMEDIATE'
            ? 'i-lucide-zap'
            : undefined
        "
        >{{ t('views.cottage.tags.online-booking') }}</UBadge
      >
      <UBadge
        v-if="pricesStore.isFreeCancellation"
        data-test="onlineBookingBadge"
        class="mt-4"
        color="success"
        variant="subtle"
        size="md"
        >{{ t('views.cottage.tags.freeCancellation') }}</UBadge
      >
    </div>
    <ULink
      data-test="toPricesButton"
      :active="true"
      class="mt-6 text-sm"
      @click="toPricesPage()"
      >{{ t('views.cottage.visualInformation.toPrices') }}</ULink
    >
  </SSection>
</template>

<script setup lang="ts">
import type { CottagePage } from '../../entities/CottagePage'
import { sendGa4CottagePricesLink } from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { usePricesStore } from '~~/modules/views/cottage-prices/stores/pricesStore'

type itemType = Pick<
  CottagePage,
  | 'slug'
  | 'rentType'
  | 'capacity'
  | 'numRooms'
  | 'numBeds'
  | 'name'
  | 'review'
  | 'reviews'
  | 'isReviewsEnabled'
> & {
  village: Pick<CottagePage['village'], 'name'>
} & {
  province: Pick<CottagePage['province'], 'name' | 'slug'>
} & {
  salesService: Pick<
    CottagePage['salesService'],
    'isOnlineBookingActive' | 'confirmationType'
  >
} & {
  hasVerifiedCalendar: CottagePage['hasVerifiedCalendar']
}

interface propsInterface {
  item: itemType
}

const props = defineProps<propsInterface>()

const route = useRoute()
const { isMobile } = useDevice()

const { dateIn, dateOut, capacity, cos } = route.query

const pricesStore = usePricesStore()

const showGoogleReviews = ref<boolean>(false)

const toPricesPage = () => {
  sendGa4CottagePricesLink({
    cottage: {
      name: props.item.name,
    },
  })
  navigateTo(
    routes().prices({
      province: props.item.province.slug,
      slug: props.item.slug,
      dateIn: typeof dateIn === 'string' ? dateIn : undefined,
      dateOut: typeof dateOut === 'string' ? dateOut : undefined,
      capacity: typeof capacity === 'string' ? capacity : undefined,
      cos: typeof cos === 'string' ? cos : undefined,
    }),
  )
}

const toogleGoogleReviewsModal = () => {
  showGoogleReviews.value = !showGoogleReviews.value
}
</script>
